/**
 * Bot Detection Service
 *
 * Detects if the current browser is likely a bot, crawler, or automated tool.
 * Useful for skipping unnecessary operations for non-human visitors.
 *
 * @example
 * import { isLikelyBot } from '@core/services/bot-detection';
 *
 * if (!isLikelyBot()) {
 *   // Only do this for real users
 *   trackAnalytics();
 * }
 */

// Known bot patterns in user agent strings
const BOT_UA_PATTERNS = [
  // Generic patterns
  /bot/i,
  /crawler/i,
  /spider/i,
  /crawling/i,

  // Headless browsers
  /headlesschrome/i,
  /phantomjs/i,

  // Search engine bots
  /googlebot/i,
  /bingbot/i,
  /yandex/i,
  /baiduspider/i,
  /duckduckbot/i,
  /slurp/i, // Yahoo

  // Social media bots
  /facebookexternalhit/i,
  /twitterbot/i,
  /linkedinbot/i,
  /whatsapp/i,
  /telegrambot/i,
  /slack/i,
  /discord/i,
  /pinterest/i,

  // SEO tools
  /semrush/i,
  /ahrefsbot/i,
  /mj12bot/i,
  /dotbot/i,
  /rogerbot/i,
  /screaming frog/i,

  // Other known bots
  /applebot/i,
  /petalbot/i,
  /bytespider/i,
  /gptbot/i,
  /chatgpt/i,
  /claudebot/i,
  /anthropic/i,
  /ccbot/i,
  /amazonbot/i,
];

/**
 * Checks if a user agent string contains known bot patterns
 */
export function hasKnownBotPattern(userAgent: string): boolean {
  return BOT_UA_PATTERNS.some((pattern) => pattern.test(userAgent));
}

/**
 * Checks if the browser is running in automated/webdriver mode
 * This is true for Puppeteer, Selenium, Playwright, etc.
 */
export function isWebDriverControlled(): boolean {
  return navigator.webdriver === true;
}

/**
 * Detects if the current browser is likely a bot or automated tool.
 *
 * Detection methods:
 * 1. navigator.webdriver - true for Puppeteer, Selenium, Playwright
 * 2. Known bot UA patterns - Googlebot, Bingbot, crawlers, social bots
 *
 * @returns true if likely a bot, false if likely a real user
 */
export function isLikelyBot(): boolean {
  if (isWebDriverControlled()) {
    return true;
  }

  const ua = navigator.userAgent;

  return hasKnownBotPattern(ua);
}

/**
 * Returns detailed bot detection results for debugging/logging
 */
export function getBotDetectionDetails(): {
  isBot: boolean;
  webdriver: boolean;
  knownBotPattern: boolean;
  userAgent: string;
} {
  const ua = navigator.userAgent;

  return {
    isBot: isLikelyBot(),
    webdriver: isWebDriverControlled(),
    knownBotPattern: hasKnownBotPattern(ua),
    userAgent: ua,
  };
}
